import i18n from "@config/i18n";
import { ASSIGNATION_DISABLED_REASON_KEYS } from "@utils/assignationLimits";
import { InvalidInput, ValidationErrorReason } from "../ISchema";
import ErrorWarning, { IErrorWarning } from "./ErrorWarning";

const ASSIGNATION_TYPE_INVALID_UDC = "ASSIGNATION_TYPE_INVALID_UDC";

interface InstructorBundleOptions {
  /** UdC threshold of the link course type when it uses the UdC limit mode. */
  udcThreshold?: number | null;
}

class InstructorBundle extends ErrorWarning {
  private error: InvalidInput;
  private udcThreshold: number | null;
  constructor(error: InvalidInput, options: InstructorBundleOptions = {}) {
    super();
    this.error = error;
    this.udcThreshold = options.udcThreshold ?? null;
  }

  private getGenericError = (): IErrorWarning =>
    this.setError(i18n.t("bundle.link-card.errors.generic"));

  private getAssignationLimitError = (): IErrorWarning =>
    this.setError(
      this.udcThreshold !== null
        ? i18n.t("bundle.link-card.errors.assignation-udc-limit", { udc: this.udcThreshold })
        : i18n.t("bundle.link-card.errors.assignation-backend.max-exceeded"),
    );

  private getMessageInvalidInput = (): IErrorWarning => {
    let invalidInput = null;
    if (this.error.__typename === "InvalidInput") {
      if ((this.error.code as string) === ASSIGNATION_TYPE_INVALID_UDC) {
        return this.setError(i18n.t(ASSIGNATION_DISABLED_REASON_KEYS["invalid-udc"]));
      }

      switch (this.error.code) {
        case ValidationErrorReason.InvalidTeachingHoursPercentage:
          const teachingHours = `La suma de los porcentajes de responsabilidad de los docentes deben sumar 100%.`;
          invalidInput = this.setError(teachingHours);
          break;
        case ValidationErrorReason.InvalidPaymentPercentage:
          const payment = `La suma de los porcentajes de carga de los docentes deben sumar 100%.`;
          invalidInput = this.setError(payment);
          break;
        case ValidationErrorReason.MustSetPrimaryInstructor:
          const primary = `Uno de los docentes de la liga debe estar marcado como principal.`;
          invalidInput = this.setError(primary);
          break;
        case ValidationErrorReason.MoreThanOnePrimaryInstructor:
          const morePrimary = `La liga debe tener solo un docente marcado como principal.`;
          invalidInput = this.setError(morePrimary);
          break;
        case ValidationErrorReason.MaxAssignationTypeExceeded:
          invalidInput = this.getAssignationLimitError();
          break;
        case ValidationErrorReason.AssignationTypeNotAllowedForCourse:
          invalidInput = this.setError(i18n.t(ASSIGNATION_DISABLED_REASON_KEYS["course-type"]));
          break;
        case ValidationErrorReason.AssignationTypePolicyConfigurationError:
          invalidInput = this.setError(
            i18n.t(ASSIGNATION_DISABLED_REASON_KEYS["invalid-configuration"]),
          );
          break;
        case ValidationErrorReason.AssignationTypeNotFound:
          invalidInput = this.setError(
            i18n.t("bundle.link-card.errors.assignation-backend.not-found"),
          );
          break;
        case ValidationErrorReason.InstructorLinkNotFound:
          invalidInput = this.setError(
            i18n.t("bundle.link-card.errors.assignation-backend.instructor-link-not-found"),
          );
          break;
        default:
          // Unknown codes still render a message: the error card cannot show `null`.
          invalidInput = this.getGenericError();
          break;
      }
    }
    return invalidInput ?? this.getGenericError();
  };

  public getMessage = () => {
    switch (this.error && this.error.__typename) {
      case "InvalidInput":
        return this.getMessageInvalidInput();
      default:
        return this.getGenericError();
    }
  };
}

export default InstructorBundle;
